import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Pagination,
  Skeleton,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { theme } from "../../../../Theme";
import { PROMO_COLORS } from "../../constants/colors";
import { CollapsibleSection } from "./CollapsibleSection";
import { EmptyState } from "./EmptyState";
import { fmtX } from "./helpers";

const HEALTH_STATUS_META = {
  healthy: { label: "Healthy", color: PROMO_COLORS.safe, bg: `${PROMO_COLORS.safe}14` },
  weak: { label: "Weak", color: PROMO_COLORS.warning, bg: `${PROMO_COLORS.warning}14` },
  critical: {
    label: "Critical",
    color: PROMO_COLORS.threat,
    bg: `${PROMO_COLORS.threat}12`,
  },
};

const CANN_RISK_META = {
  low: { label: "Low", color: PROMO_COLORS.safe },
  med: { label: "Med", color: PROMO_COLORS.warning },
  high: { label: "High", color: PROMO_COLORS.threat },
  none: { label: "—", color: "#94A3B8" },
};

const SKU_TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SKU_TABLE_DEFAULT_PAGE_SIZE = 10;

const SKU_TABLE_COLUMNS = [
  { id: "sku", label: "SKU", numeric: false },
  { id: "actualVolume", label: "Actual Volume", numeric: true },
  { id: "volumeTrend", label: "Volume Trend", numeric: true },
  { id: "cmTrend", label: "CM Trend", numeric: true },
  { id: "avgRoi", label: "ROI", numeric: true },
  { id: "avgDiscount", label: "Avg Discount", numeric: true },
  { id: "promoDependencyPct", label: "Promo Dependency", numeric: true },
  { id: "cannibalizationRisk", label: "Cannibalization Risk", numeric: true },
  { id: "health", label: "Health Status", numeric: true },
];

function HealthStatusChip({ status }) {
  const m = HEALTH_STATUS_META[status] || HEALTH_STATUS_META.weak;
  return (
    <Chip
      label={m.label}
      size="small"
      sx={{
        fontSize: 11,
        fontWeight: 700,
        color: m.color,
        bgcolor: m.bg,
        border: `1px solid ${m.color}44`,
      }}
    />
  );
}

function CannibalRiskChip({ tier, label }) {
  const m = CANN_RISK_META[tier] || CANN_RISK_META.none;
  if (tier === "none") {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13 }}>
        —
      </Typography>
    );
  }
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontSize: 11,
        fontWeight: 700,
        color: m.color,
        bgcolor: `${m.color}12`,
        border: `1px solid ${m.color}44`,
      }}
    />
  );
}

function TrendPctCell({ value }) {
  if (value == null || !Number.isFinite(Number(value))) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13 }}>
        —
      </Typography>
    );
  }
  const n = Number(value);
  const color =
    n > 0 ? PROMO_COLORS.safe : n < 0 ? PROMO_COLORS.threat : "text.secondary";
  const formatted = n.toLocaleString("en-US", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
  const text = n > 0 ? `+${formatted}%` : `${formatted}%`;
  return (
    <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600, color }}>
      {text}
    </Typography>
  );
}

function descendingComparator(a, b, orderBy) {
  const risk = (t) => (t === "high" ? 2 : t === "med" ? 1 : t === "low" ? 0 : -1);
  const healthR = (h) =>
    h === "critical" ? 2 : h === "weak" ? 1 : h === "healthy" ? 0 : -1;

  let av;
  let bv;

  switch (orderBy) {
    case "sku":
      if (String(b.sku).toLowerCase() < String(a.sku).toLowerCase()) return -1;
      if (String(b.sku).toLowerCase() > String(a.sku).toLowerCase()) return 1;
      return 0;
    case "actualVolume":
      av = a.actualVolume ?? -Infinity;
      bv = b.actualVolume ?? -Infinity;
      break;
    case "volumeTrend":
      av = a.volumeTrendPct ?? -Infinity;
      bv = b.volumeTrendPct ?? -Infinity;
      break;
    case "cmTrend":
      av = a.cmTrendPct ?? -Infinity;
      bv = b.cmTrendPct ?? -Infinity;
      break;
    case "avgRoi":
      av = a.avgRoi ?? -Infinity;
      bv = b.avgRoi ?? -Infinity;
      break;
    case "avgDiscount":
      av = a.avgDiscount ?? -Infinity;
      bv = b.avgDiscount ?? -Infinity;
      break;
    case "promoDependencyPct":
      av = a.promoDependencyPct ?? -Infinity;
      bv = b.promoDependencyPct ?? -Infinity;
      break;
    case "cannibalizationRisk":
      av = risk(a.cannibalRiskTier);
      bv = risk(b.cannibalRiskTier);
      break;
    case "health":
      av = healthR(a.health);
      bv = healthR(b.health);
      break;
    default:
      av = a.actualVolume ?? -Infinity;
      bv = b.actualVolume ?? -Infinity;
  }

  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function SKUHealthDiagnosticsTable({ skuHealth, loading, filterBar }) {
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("actualVolume");
  const [search, setSearch] = useState("");
  const [healthFilter, setHealthFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(SKU_TABLE_DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, [search, healthFilter, skuHealth]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return skuHealth.filter((row) => {
      if (healthFilter !== "all" && row.health !== healthFilter) return false;
      if (q && !String(row.sku).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [skuHealth, search, healthFilter]);

  const sortedRows = useMemo(
    () => [...filtered].sort(getComparator(order, orderBy)),
    [filtered, order, orderBy]
  );

  const totalFiltered = sortedRows.length;
  const pageCount = Math.max(1, Math.ceil(totalFiltered / rowsPerPage));

  useEffect(() => {
    if (page > 0 && page >= pageCount) {
      setPage(Math.max(0, pageCount - 1));
    }
  }, [page, pageCount]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  const paginationCount = Math.ceil(totalFiltered / rowsPerPage);
  const hasData = skuHealth.length > 0;

  return (
    <CollapsibleSection
      icon={<MonitorHeartOutlinedIcon sx={{ fontSize: 20 }} />}
      title="SKU Health & Diagnostics"
      subtitle="Per-SKU performance from historical data — sort columns, search SKU, or filter by health"
      filterBar={filterBar}
    >
      {!loading && hasData && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            placeholder="Search SKU…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: { sm: 1 }, maxWidth: { sm: 360 } }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Health status</InputLabel>
            <Select
              label="Health status"
              value={healthFilter}
              onChange={(e) => setHealthFilter(e.target.value)}
            >
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="healthy">Healthy</MenuItem>
              <MenuItem value="weak">Weak</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}

      {loading ? (
        <Stack spacing={1.25}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={44} />
          ))}
        </Stack>
      ) : !hasData ? (
        <EmptyState message="No SKU health data for the selected filters. Adjust brand, year, or week range and try again." />
      ) : sortedRows.length === 0 ? (
        <EmptyState message="No SKUs match your search or health filter. Try clearing filters." />
      ) : (
        <>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="ph-sku-rows-per-page">Rows per page</InputLabel>
              <Select
                labelId="ph-sku-rows-per-page"
                label="Rows per page"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
              >
                {SKU_TABLE_PAGE_SIZE_OPTIONS.map((n) => (
                  <MenuItem key={`sku-rpp-${n}`} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <TableContainer
            sx={{ borderRadius: 1.5, border: "1px solid #EEF1F4", overflowX: "hidden" }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                tableLayout: "fixed",
                "& .MuiTableCell-root": {
                  borderColor: "#EEF1F4",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#F4F6F9" }}>
                  {SKU_TABLE_COLUMNS.map((col) => (
                    <TableCell
                      key={col.id}
                      sortDirection={orderBy === col.id ? order : false}
                      sx={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: theme.palette.primary.main,
                        whiteSpace: "nowrap",
                        py: 1.25,
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : "asc"}
                        onClick={() => handleRequestSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row) => (
                  <TableRow
                    key={row.skuId != null ? `sku-${row.skuId}` : row.sku}
                    hover
                    sx={{
                      "&:hover": { bgcolor: "rgba(25,118,210,.04)" },
                      "&:last-of-type td": { borderBottom: 0 },
                      ...(row.health === "critical"
                        ? { bgcolor: `${PROMO_COLORS.threat}06` }
                        : row.health === "weak"
                        ? { bgcolor: `${PROMO_COLORS.warning}05` }
                        : {}),
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>
                      {row.sku}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {row.actualVolume != null
                        ? `${Number(row.actualVolume).toLocaleString("en-US", {
                            maximumFractionDigits: 0,
                          })} L`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <TrendPctCell value={row.volumeTrendPct} />
                    </TableCell>
                    <TableCell>
                      <TrendPctCell value={row.cmTrendPct} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{fmtX(row.avgRoi)}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {row.avgDiscount != null
                        ? `${Number(row.avgDiscount).toFixed(0)}%`
                        : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {row.promoDependencyPct != null
                        ? `${row.promoDependencyPct}%`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <CannibalRiskChip
                        tier={row.cannibalRiskTier}
                        label={row.cannibalizationRisk}
                      />
                    </TableCell>
                    <TableCell>
                      <HealthStatusChip status={row.health} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {paginationCount >= 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 2,
                borderTop: "1px solid #EEF1F4",
              }}
            >
              <Pagination
                count={paginationCount}
                page={page + 1}
                onChange={(_e, value) => setPage(value - 1)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  "& .MuiPaginationItem-root": { fontWeight: 500, fontSize: 13 },
                  "& .MuiPaginationItem-page.Mui-selected": { fontWeight: 700 },
                }}
              />
            </Box>
          )}
        </>
      )}
    </CollapsibleSection>
  );
}
